import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { match } from 'ts-pattern'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Header from '@/components-latest/layout/Header'
import Button from '@/components-latest/primitives/Button'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import { ExclamationPoint, Logout } from '@/components-latest/primitives/Icons'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import Accordion from '@/components-latest/shared/Accordion'
import ConfirmationModal from '@/components-latest/shared/Modals/ConfirmationModal'
import BusinessHoursForm from '@/components-latest/ui/AccountPage/BusinessHoursForm'
import BusinessHoursForm5Slots from '@/components-latest/ui/AccountPage/BusinessHoursForm5Slots'
import EditStoreInfoForm from '@/components-latest/ui/AccountPage/EditStoreInfoForm'
import PreparationTimeForm from '@/components-latest/ui/AccountPage/PreparationTimeForm/PreparationTimeForm'
import StoreInfoBlock from '@/components-latest/ui/AccountPage/StoreInfoBlock'
import {
  useLazyFetchBusinessHoursQuery,
  useLazyFetchPreparationTimeQuery,
  useLazyFetchStoreInfoQuery
} from '@/ducks/account/api/account.api'
import { logout } from '@/ducks/auth/slice/auth.slice'
import { clearOrderNotifications } from '@/ducks/notifications/slices/notification.slice'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { useToggle } from '@/hooks/useToggle/useToggle'

import CalendarFilled from '../../components-latest/primitives/Icons/CalendarFilled'
import Styles from './AccountPage.module.scss'

const AccountPage = () => {
  const { t } = useTranslation()
  const { store_web_app_additional_time_slots } = useFlags()

  const dispatch = useDispatch()

  const storeId = useSelector(selectStoreId)

  const [isEditFormVisible, toggleEditFormVisibility] = useToggle(false)

  const [fetchStoreInfo, { data, isFetching }] = useLazyFetchStoreInfoQuery()
  const [fetchBusinessHours, fetchBusinessHoursQuery] =
    useLazyFetchBusinessHoursQuery()

  const [
    fetchPreparationTime,
    { data: preparationTime, isFetching: isPreparationLoading }
  ] = useLazyFetchPreparationTimeQuery()

  useEffect(() => {
    if (storeId) {
      fetchStoreInfo({ storeId })
      fetchPreparationTime({ storeId })
      fetchBusinessHours({ storeId })
    }
  }, [storeId, fetchStoreInfo, fetchPreparationTime, fetchBusinessHours])

  const onLogout = () => {
    dispatch(logout())
    dispatch(clearOrderNotifications())
  }

  return (
    <div className={Styles.page}>
      <Header
        onGoBack={isEditFormVisible ? toggleEditFormVisibility : undefined}
        title={
          isEditFormVisible
            ? t('account.edit-info.header')
            : t('common.page-names.account')
        }>
        <ConfirmationModal
          title={t('account.logout.title')}
          description={t('account.logout.description')}
          confirmButtonText={t('account.logout.confirm')}
          onConfirm={onLogout}>
          <Button variant="tertiary" icon={<Logout />}>
            {t('account.logout.trigger')}
          </Button>
        </ConfirmationModal>
      </Header>
      {!isFetching && data && data.inReview && (
        <div className={Styles.inReview}>
          <ExclamationPoint />
          <Text variant="body" weight="semiBold">
            {t('account.in-review')}
          </Text>
        </div>
      )}
      <div className={Styles.content}>
        {match({ isFetching, data, isEditFormVisible })
          .when(
            ({ data, isFetching }) => !data || isFetching,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .with({ isEditFormVisible: true }, () => (
            <EditStoreInfoForm
              initialStoreInfo={data}
              closeEditForm={toggleEditFormVisibility}
            />
          ))
          .with({ isEditFormVisible: false }, () => (
            <>
              <StoreInfoBlock
                onEditClick={toggleEditFormVisibility}
                isLoading={isFetching}
                storeProfile={data}
              />
              <PreparationTimeForm
                isLoading={isPreparationLoading}
                initPreparationData={preparationTime?.range}
              />
              <ContentBlock>
                <Accordion
                  icon={<CalendarFilled />}
                  label={t('account.business-hours.title')}>
                  {match(fetchBusinessHoursQuery)
                    .with({ isLoading: true }, () => (
                      <ContentBlock className={Styles.hoursSpinner}>
                        <Spinner />
                      </ContentBlock>
                    ))
                    .with({ isSuccess: true }, ({ data }) =>
                      store_web_app_additional_time_slots ? (
                        <BusinessHoursForm5Slots initialData={data} />
                      ) : (
                        <BusinessHoursForm initialData={data} />
                      )
                    )
                    .otherwise(() => null)}
                </Accordion>
              </ContentBlock>
            </>
          ))
          .otherwise(() => null)}
      </div>
    </div>
  )
}

export default AccountPage
